import React, { useEffect, useState } from 'react';
import { randUuid } from 'utils/functions';
import classNames from 'classnames';
import Tooltip from '@mui/material/Tooltip';
import { FormControlLabel, Switch } from '@mui/material';

/**
 * This is a complex component that can render a label accompanied by following siblings:
 * - info icon with tooltip (label-with-info)
 * - a checkbox input (label-with-checkbox)
 * - a radio input (label-with-radio)
 * @param siblingsMask This is a mandatory parameter which describes the siblings and their order.
 * The parameter can contain following letters:
 * - C => will add a checkbox
 * - R => will add a radio
 * - S => will add a MUI Switch (https://mui.com/material-ui/react-switch/)
 * - I => will add info icon with tooltip; Note: the icon will be rendered only if the tooltipContent option is provided
 * - L => indicates the position of the label element
 * The order of letters will provide the order of the siblings. Example: LR => label with radio input after, CL => a checkbox followed by the label
 * Should not provide both C, R in the options because both will inherit same properties.
 * @param labelContent The content to be displayed in the label. It can be a string, or another react component. (mandatory)
 * @param labelClassNames A list of CSS class names to be added over the label container. (optional)
 * @param tooltipContent The content of the tooltip. Mandatory if I letter is provided in siblingsMask
 * @param tooltipPlacement The placement of the tooltip (top, left, ...). See:https://mui.com/material-ui/react-tooltip/ for options (optional)
 * @param {string} variation The value can be: round. When this property is given, the input is replaced with two span elements that fakes an input.
 * @param inputProps All other properties will be assimilated as input properties. Ex: onChange, disabled ...
 * @return {JSX.Element}
 * @constructor
 */
function LabelWithSiblings({
    siblingsMask,
    labelContent,
    labelClassNames,
    tooltipContent,
    tooltipPlacement,
    variation = 'none',
    ...inputProps
}) {
    siblingsMask = siblingsMask.toUpperCase();
    const uniqueId = `label-with-siblings-${randUuid()}`;
    const maksElements = siblingsMask.split('');
    const hasInput = maksElements.indexOf('C') >= 0 || maksElements.indexOf('R') >= 0 || maksElements.indexOf('S') >= 0;

    const [isChecked, setIsChecked] = useState(false);

    useEffect(() => {
        setIsChecked(inputProps.checked === true);
    }, [inputProps.checked]);

    const handleChange = (e) => {
        setIsChecked(e.target.checked);

        if (typeof inputProps.onChange !== 'undefined') {
            inputProps.onChange(e);
        }
    };

    const renderInfo = () => {
        if (typeof tooltipContent === 'undefined') {
            return '';
        }

        const placement = typeof tooltipPlacement === 'undefined' ? 'right' : tooltipPlacement;

        return (
            <div className="info-container" key={`info-${uniqueId}`}>
                <Tooltip title={tooltipContent} placement={placement} arrow>
                    <span className="stw-information" />
                </Tooltip>
            </div>
        );
    };

    const renderCheckbox = () => {
        return (
            <div className="input-container" key={`checkbox-${uniqueId}`}>
                <input type="checkbox" id={uniqueId} {...inputProps} checked={isChecked} onChange={handleChange} />
                {variation === 'round' && isChecked && <span className={'stw-checked'}></span>}
                {variation === 'round' && !isChecked && <span className={'stw-unchecked'}></span>}
            </div>
        );
    };

    const renderRadio = () => {
        return (
            <div className="input-container" key={`radio-${uniqueId}`}>
                <input type="radio" id={uniqueId} {...inputProps} />
                {variation === 'round' && isChecked && <span className={'stw-radio-checked'}></span>}
                {variation === 'round' && !isChecked && <span className={'stw-unchecked'}></span>}
            </div>
        );
    };

    const renderSwitcher = () => {
        return <Switch id={uniqueId} key={`switcher-${uniqueId}`} {...inputProps}></Switch>;
    };

    const renderLabel = () => {
        return (
            <div
                className={classNames('label-container', labelClassNames ?? '', {
                    'wcf-ui-disabled-control': !!inputProps.disabled,
                })}
                key={`label-${uniqueId}`}
            >
                {labelContent}
                {hasInput && <label className="hidden-for-label" htmlFor={uniqueId}></label>}
            </div>
        );
    };

    return (
        <div
            className={`wcf-ui-label-with-siblings ${maksElements.join(' ')} ${variation} ${!!inputProps.disabled ? 'disabled' : ''}`}
            {...inputProps}
        >
            {maksElements.map((siblingType) => {
                switch (siblingType) {
                    case 'I':
                        return renderInfo();

                    case 'C':
                        return renderCheckbox();

                    case 'R':
                        return renderRadio();

                    case 'S':
                        return renderSwitcher();

                    case 'L':
                        return renderLabel();

                    default:
                        // invalid case
                        return '';
                }
            })}
        </div>
    );
}

export default LabelWithSiblings;
